import React from 'react';

import {
  AlertCounter,
  AlertsSummary,
  DeviceInactivity,
} from '../../../../entities/Alert';

import {
  Band,
  BandFill,
  BandTrack,
  Card,
  CardNote,
  CardTitle,
  CounterCard,
  Counters,
  InactivityBands,
  Variation,
} from '../styles';

interface SummaryProps {
  summary?: AlertsSummary;
  inactivity?: DeviceInactivity;
  /** A janela dos contadores, dita no título ("Últimos 30 dias"). */
  periodLabel: string;
  /** O recorte do filtro "Situação" — o mesmo da lista abaixo. */
  situationLabel: string;
  /**
   * A consulta falhou. Os números viram "—" em vez de 0: zero é resposta, e a tela
   * não sabe a resposta.
   */
  failed?: boolean;
}

/**
 * Os contadores, com as cores de `02-alertas.md`.
 *
 * ⓘ **O quinto, "Pessoas envolvidas", veio do painel de contingência** (2026-09-24).
 * Conta pacientes **distintos** — não é a soma de Grave e Moderado, porque quem tem
 * PA e SpO₂ alterados conta duas vezes lá e uma aqui.
 *
 * ⚠️ **Grave e Moderado são gravidade; Engajamento é categoria.** O bloco mistura
 * os dois eixos de propósito, então **a soma dos três não fecha com o total** — um
 * alerta de engajamento grave conta em dois. (O cartão Emocional saiu em
 * 2026-10-07, no ajuste A4: o produto não tem alerta emocional.) A tela não exibe
 * "total" ao lado deles justamente para não convidar ninguém a somar e concluir que
 * há um erro.
 */
type CounterKey = {
  [K in keyof AlertsSummary]: AlertsSummary[K] extends AlertCounter ? K : never;
}[keyof AlertsSummary];

const COUNTERS: { key: CounterKey; label: string; color: string }[] = [
  { key: 'severe', label: 'Grave', color: '#EA7676' },
  { key: 'moderate', label: 'Moderado', color: '#FA8822' },
  { key: 'engagement', label: 'Engajamento', color: '#6FCF97' },
  { key: 'patientsInvolved', label: 'Pessoas envolvidas', color: '#1F8DED' },
];

const formatVariation = (counter: AlertCounter): string => {
  if (counter.variation === 0) {
    return 'sem variação vs. período anterior';
  }

  const sinal = counter.variation > 0 ? '+' : '';

  return `${sinal}${counter.variation} vs. período anterior`;
};

/**
 * As faixas do painel de inatividade, em **dias**.
 *
 * ⚠️ **Eram horas até 2026-08-28** (até 12h / 12–24h / mais de 24h). O AJ-33 decidiu
 * em 16/08 que a régua de inatividade é em dias, e diz que este bloco acompanha.
 *
 * ⓘ **As duas primeiras faixas não geram alerta** — são mensagem proativa da CintIA.
 * Elas aparecem porque a enfermagem precisa saber que alguém já está sendo puxado
 * antes de virar caso dela; sem isso, um paciente reapareceria do nada aos 14 dias.
 */
const INACTIVITY_BANDS: {
  key: keyof Omit<DeviceInactivity, 'signal' | 'never'>;
  label: string;
  color: string;
}[] = [
  { key: 'upTo3d', label: 'Até 3 dias', color: '#6FCF97' },
  { key: 'from3dTo7d', label: 'De 3 a 7 dias', color: '#A8D8A0' },
  { key: 'from7dTo14d', label: 'De 7 a 14 dias', color: '#F2C94C' },
  {
    key: 'from14dTo30d',
    label: 'De 14 a 30 dias · moderado',
    color: '#FA8822',
  },
  { key: 'over30d', label: 'Mais de 30 dias · grave', color: '#EA7676' },
];

const Summary: React.FC<SummaryProps> = ({
  summary,
  inactivity,
  periodLabel,
  situationLabel,
  failed = false,
}) => {
  const shown = (value: number | undefined): number | string =>
    failed ? '—' : value ?? 0;

  const totalInactivity = inactivity
    ? INACTIVITY_BANDS.reduce((sum, band) => sum + inactivity[band.key], 0)
    : 0;

  const percent = (value: number): number =>
    totalInactivity === 0 ? 0 : Math.round((value / totalInactivity) * 100);

  return (
    <>
      <Card>
        {/*
          O recorte no título: os cartões seguem o filtro "Situação" da lista, e
          dizê-lo é o que impede "4 graves" em cima de uma lista com 1 de parecer
          defeito quando o filtro é outro.
        */}
        <CardTitle>
          Números de alertas · {situationLabel} · {periodLabel}
        </CardTitle>
        <CardNote>
          Grave e Moderado são gravidade; Engajamento é categoria — um alerta
          pode contar em dois cartões.
        </CardNote>

        <Counters>
          {COUNTERS.map(({ key, label, color }) => {
            const counter = summary?.[key];

            return (
              <CounterCard key={key} color={color}>
                <span>{label}</span>
                <strong>{shown(counter?.current)}</strong>
                {counter && (
                  <Variation positive={counter.variation > 0}>
                    {formatVariation(counter)}
                  </Variation>
                )}
              </CounterCard>
            );
          })}
        </Counters>
      </Card>

      <Card>
        <CardTitle>Gestão de inatividade</CardTitle>
        <CardNote>
          Dias desde a última medição recebida do dispositivo. Alerta a partir
          de 14 dias.
        </CardNote>

        <InactivityBands>
          {INACTIVITY_BANDS.map(({ key, label, color }) => (
            <Band key={key}>
              <span>{label}</span>
              <BandTrack>
                <BandFill
                  percent={percent(inactivity?.[key] ?? 0)}
                  color={color}
                />
              </BandTrack>
              <b>{shown(inactivity?.[key])}</b>
            </Band>
          ))}
          {/*
            Fora das faixas, e com rótulo próprio: são os pacientes sem kit. Somá-los
            ao pior balde faria o painel dizer que a maior parte da coorte está
            inativa desde sempre.
          */}
          <Band>
            <span>Sem dispositivo</span>
            <BandTrack>
              <BandFill percent={0} color="#BDBDBD" />
            </BandTrack>
            <b>{shown(inactivity?.never)}</b>
          </Band>
        </InactivityBands>
      </Card>
    </>
  );
};

export default Summary;
